import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, FileText, VideoCamera, Headphones, ChatCircleText, PencilSimple, XCircle, EyeSlash, Trash, CaretDown, ListChecks } from '@phosphor-icons/react';
import MaterialEditModal from './MaterialEditModal';
import RecordingEditModal from './RecordingEditModal';
import ExerciseEditModal from './ExerciseEditModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  material: { label: 'Material', icon: FileText, color: 'text-[#AB1C24] bg-[#AB1C24]/10' },
  recording: { label: 'Aufnahme', icon: VideoCamera, color: 'text-[#1F3A44] bg-[#5B8C9A]/15' },
  exercise: { label: 'Hörverstehen', icon: Headphones, color: 'text-amber-800 bg-amber-100' },
  topic: { label: 'Thema', icon: ChatCircleText, color: 'text-emerald-800 bg-emerald-100' },
};

const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((new Date(d).setHours(0, 0, 0, 0) - today) / 86400000);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Morgen';
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' });
};

export default function ScheduledPublications() {
  const [data, setData] = useState({ upcoming: [], drafts: [] });
  const [editing, setEditing] = useState(null);
  const [cancelItem, setCancelItem] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await axios.get(`${API}/teacher/scheduled?days=7`, { withCredentials: true });
      setData(res.data);
    } catch { /* non-critical widget */ }
  };
  useEffect(() => { load(); }, []);

  const openEditor = async (item) => {
    try {
      const res = await axios.get(`${API}/teacher/scheduled/${item.kind}/${item.id}`, { withCredentials: true });
      if (item.kind === 'topic') {
        const base = res.data.category === 'writing' ? '/schreiben' : '/community';
        navigate(`${base}?topic=${item.id}&edit=1`);
        return;
      }
      setEditing({ kind: item.kind, doc: res.data });
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const groups = data.upcoming.reduce((acc, it) => {
    const k = dayLabel(it.scheduled_at);
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});
  const closeEditor = () => setEditing(null);

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4 sm:p-5" data-testid="scheduled-publications">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="font-heading font-bold text-[#292524] flex items-center gap-2">
          <CalendarCheck size={22} weight="duotone" className="text-[#AB1C24]" />
          Geplante Veröffentlichungen · nächste 7 Tage
        </h3>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-stone-100 text-stone-700" data-testid="scheduled-count">{data.upcoming.length}</span>
      </div>
      {data.upcoming.length === 0 ? (
        <p className="text-sm text-[#78716C]" data-testid="scheduled-empty">Für die nächsten 7 Tage ist nichts geplant.</p>
      ) : (
        <div className="space-y-4">
          {Object.entries(groups).map(([day, items]) => (
            <div key={day}>
              <p className="text-[11px] uppercase tracking-wider font-bold text-[#A8A29E] mb-1.5">{day}</p>
              <div className="space-y-1.5">
                {items.map(it => <ScheduledRow key={`${it.kind}-${it.id}`} it={it} onEdit={openEditor} onCancel={setCancelItem} />)}
              </div>
            </div>
          ))}
        </div>
      )}
      {data.drafts.length > 0 && (
        <div className="mt-4 pt-3 border-t border-stone-100">
          <button onClick={() => setShowDrafts(s => !s)} className="text-xs font-semibold text-[#57534E] flex items-center gap-1" data-testid="scheduled-drafts-toggle">
            <EyeSlash size={14} /> Ausgeblendete Entwürfe ({data.drafts.length})
            <CaretDown size={12} className={`transition-transform ${showDrafts ? 'rotate-180' : ''}`} />
          </button>
          {showDrafts && (
            <div className="space-y-1.5 mt-2">
              {data.drafts.map(it => <ScheduledRow key={`${it.kind}-${it.id}`} it={it} onEdit={openEditor} onCancel={setCancelItem} draft />)}
            </div>
          )}
        </div>
      )}
      {cancelItem && <CancelDialog item={cancelItem} onClose={() => setCancelItem(null)} onDone={load} />}
      <MaterialEditModal material={editing?.kind === 'material' ? editing.doc : null} open={editing?.kind === 'material'} onClose={closeEditor} onSaved={load} />
      <RecordingEditModal recording={editing?.kind === 'recording' ? editing.doc : null} open={editing?.kind === 'recording'} onClose={closeEditor} onSaved={load} />
      <ExerciseEditModal exercise={editing?.kind === 'exercise' ? editing.doc : null} open={editing?.kind === 'exercise'} onClose={closeEditor} onSaved={load} />
    </div>
  );
}

const ScheduledRow = ({ it, onEdit, onCancel, draft }) => {
  const meta = it.kind === 'exercise' && it.exercise_type !== 'listening'
    ? { ...KIND_META.exercise, label: 'Übung', icon: ListChecks }
    : KIND_META[it.kind];
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-stone-200 px-3 py-2 hover:border-stone-300 transition-colors" data-testid={`scheduled-item-${it.kind}-${it.id}`}>
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.color}`}><Icon size={16} weight="fill" /></span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#292524] truncate">{it.title}</p>
        <p className="text-[11px] text-[#78716C]">
          {meta.label}{it.levels?.length ? ` · ${it.levels.join(', ')}` : ''}
          {draft ? ' · Entwurf (für Schüler unsichtbar)' : ` · ${new Date(it.scheduled_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`}
        </p>
      </div>
      <button onClick={() => onEdit(it)} className="p-2 rounded-lg hover:bg-stone-100 text-[#57534E]" title={draft ? 'Bearbeiten / neu planen' : 'Bearbeiten'} data-testid={`scheduled-edit-${it.id}`}>
        <PencilSimple size={16} />
      </button>
      <button onClick={() => onCancel({ ...it, draft })} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title={draft ? 'Löschen' : 'Veröffentlichung abbrechen'} data-testid={`scheduled-cancel-${it.id}`}>
        {draft ? <Trash size={16} /> : <XCircle size={16} />}
      </button>
    </div>
  );
};

const CancelDialog = ({ item, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const run = async (mode) => {
    if (mode === 'delete' && !window.confirm(`„${item.title}“ endgültig löschen? Das kann nicht rückgängig gemacht werden.`)) return;
    setBusy(true);
    try {
      if (mode === 'draft') await axios.post(`${API}/teacher/scheduled/${item.kind}/${item.id}/draft`, {}, { withCredentials: true });
      else await axios.delete(`${API}/teacher/scheduled/${item.kind}/${item.id}`, { withCredentials: true });
      toast.success(mode === 'draft' ? 'Als Entwurf ausgeblendet' : 'Endgültig gelöscht');
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && onClose()} data-testid="scheduled-cancel-dialog">
      <div className="bg-white rounded-2xl max-w-sm w-full p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <h4 className="font-heading font-bold text-[#292524]">{item.draft ? 'Entwurf löschen?' : 'Veröffentlichung abbrechen'}</h4>
        <p className="text-sm text-[#57534E]">„{item.title}“ — was soll damit passieren?</p>
        {!item.draft && (
          <button disabled={busy} onClick={() => run('draft')} className="w-full text-left rounded-xl border border-stone-200 hover:border-[#5B8C9A] p-3 transition-colors" data-testid="scheduled-cancel-draft-btn">
            <span className="font-semibold text-sm flex items-center gap-2"><EyeSlash size={16} /> Als Entwurf ausblenden</span>
            <span className="block text-xs text-[#78716C]">Schüler sehen es nicht. Du kannst es später neu planen.</span>
          </button>
        )}
        <button disabled={busy} onClick={() => run('delete')} className="w-full text-left rounded-xl border border-red-200 hover:border-red-400 p-3 transition-colors" data-testid="scheduled-cancel-delete-btn">
          <span className="font-semibold text-sm text-red-600 flex items-center gap-2"><Trash size={16} /> Endgültig löschen</span>
          <span className="block text-xs text-[#78716C]">Wird komplett entfernt.</span>
        </button>
        <button disabled={busy} onClick={onClose} className="w-full py-2 text-sm font-semibold text-[#57534E] hover:bg-stone-50 rounded-xl" data-testid="scheduled-cancel-close-btn">Zurück</button>
      </div>
    </div>
  );
};
